import type { PendingComments } from '@/data/v2/pr/pending-comments-types';

import type { submitPrV2Review } from '../fetch-pr-v2';

import { CommentSubjectType } from '../pending-comments-types';
import { commentSideToGithubReviewSide } from './review-submit-optimistic';

export function remapFailedPendingCommentParent(
  pendingComment: PendingComments,
  idMapping: Record<string, number>,
): PendingComments {
  if (!('in_reply_to_id' in pendingComment) || !pendingComment.parent_is_pending) {
    return pendingComment;
  }

  const remappedParentId = idMapping[String(pendingComment.in_reply_to_id)];
  if (
    remappedParentId == null ||
    !Number.isSafeInteger(remappedParentId) ||
    remappedParentId <= 0
  ) {
    return pendingComment;
  }

  return {
    ...pendingComment,
    in_reply_to_id: remappedParentId,
    parent_is_pending: false,
  };
}

export function filterPendingCommentsForPr(
  pendingComments: PendingComments[],
  normalizedOwner: string,
  normalizedRepo: string,
  pullNumber: number,
): PendingComments[] {
  return pendingComments.filter(
    (pendingComment) =>
      pendingComment.owner.toLowerCase() === normalizedOwner &&
      pendingComment.repo.toLowerCase() === normalizedRepo &&
      pendingComment.pullNumber === pullNumber,
  );
}

export function formatSubmitReviewComments(
  pendingCommentsSnapshot: PendingComments[],
): Parameters<typeof submitPrV2Review>[3]['comments'] {
  // The review submit API treats replies differently from base comments and expects file comments
  // to omit `line` entirely while line comments include side/range metadata when available.
  return pendingCommentsSnapshot.map((pendingComment) => {
    if ('in_reply_to_id' in pendingComment) {
      return {
        id: pendingComment.id,
        body: pendingComment.content,
        in_reply_to: pendingComment.in_reply_to_id,
        parent_is_pending: pendingComment.parent_is_pending,
      };
    }

    const subjectType = pendingComment.subject_type ?? CommentSubjectType.line;
    const startSide = pendingComment.start_side
      ? commentSideToGithubReviewSide(pendingComment.start_side)
      : undefined;

    return {
      id: pendingComment.id,
      body: pendingComment.content,
      commit_id: pendingComment.commit_id,
      path: pendingComment.filename,
      ...(subjectType === CommentSubjectType.file ? {} : { line: pendingComment.lineNumber }),
      side: commentSideToGithubReviewSide(pendingComment.side),
      subject_type: subjectType,
      ...(pendingComment.start_line ? { start_line: pendingComment.start_line } : {}),
      ...(startSide ? { start_side: startSide } : {}),
    };
  });
}
